import React, { useState } from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  InputAdornment,
  IconButton
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Visibility, VisibilityOff, AccountCircle, Lock } from '@material-ui/icons';
import { useAuth } from '../context/AuthContext';

const styles = (theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    padding: theme.spacing.unit * 2,
  },
  card: {
    maxWidth: 400,
    width: '100%',
    padding: theme.spacing.unit * 2,
  },
  logo: {
    textAlign: 'center',
    marginBottom: theme.spacing.unit * 3,
  },
  logoText: {
    fontSize: '2rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  submitBtn: {
    marginTop: theme.spacing.unit * 2,
    padding: theme.spacing.unit * 1.5,
  },
  textField: {
    marginBottom: theme.spacing.unit * 2,
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing.unit,
    textAlign: 'center',
  },
});

const Login = ({ classes }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login, isAuthenticated } = useAuth();
  const history = useHistory();

  // Redirect if already logged in
  if (isAuthenticated()) {
    return <Redirect to="/" />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      history.push('/');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={classes.root}>
      <Card className={classes.card}>
        <CardContent>
          <Box className={classes.logo}>
            <Typography className={classes.logoText}>
              MGM Algo-Trader
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Sign in to continue
            </Typography>
          </Box>

          <form onSubmit={handleSubmit} className={classes.form}>
            <TextField
              className={classes.textField}
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              className={classes.textField}
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Typography className={classes.errorText} variant="body2">
                {error}
              </Typography>
            )}

            <Button
              className={classes.submitBtn}
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        message={error}
      />
    </Box>
  );
};

export default withStyles(styles)(Login);
